/**
 * Fragment Library Setup Action Definitions
 *
 * Phase 4 of `gibbs-bmaps-capability-coverage`. Binds 4 unreachable
 * fragment-data UserActions in `proto/src/cmds/FragmentsCmds.js`. The
 * `LoadFragmentSeries` binding closes the user's "load the kinase
 * fragment library" pathway — the canonical example named when the
 * coverage gap was first surfaced 2026-05-03.
 *
 * **Decision: LLM-tier dispatch, not a dedicated shortcut.**
 * `LoadFragmentSeries` takes a list of `FragSeriesInfo` objects (rich
 * BMaps-state objects with project / case / load metadata), not
 * symbolic library names. A `loadFragmentLibraryShortcut.js` mirroring
 * the Tier 11 template loader pattern would need a Gibbs-side registry
 * mapping `'kinase'` / `'GPCR'` / etc. → `FragSeriesInfo`, which does
 * not exist. Documented as a follow-on in this study's `followups.md`.
 * The LLM tier dispatches based on rankActions retrieval; the workflow
 * checker UI prompts for the FragSeriesInfo input at runtime.
 *
 * @module gibbs/registry/actions/fragment-library-setup
 */

import { param, pre } from '../schema';

// ─── Library load (THE canonical "load the kinase fragment library" ) ──────

export const loadFragmentSeries = {
    id: 'load-fragment-series',
    name: 'Load Fragment Library Series',
    category: 'Fragment Operations',
    description: 'Load a fragment library (kinase, GPCR, Meanwell, etc.) into the workspace as a fragment series. Each series is a curated collection of fragments derived from a target family, ready for fragment growing or hotspot analysis. Use when the user says "load the kinase fragment library" or "bring in the GPCR fragments".',
    inputs: [
        param('fragSeriesList', 'object', 'List of FragSeriesInfo objects naming the libraries to load', true),
    ],
    outputs: [
        param('responseData', 'object', 'Server response with the loaded fragment data'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded before fragment libraries can be associated'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'LoadFragmentSeries',
    serverCommand: null,
    jobType: null,
};

// ─── Fragment data refresh ─────────────────────────────────────────────────

export const refreshAllFragments = {
    id: 'refresh-all-fragments',
    name: 'Refresh All Fragments',
    category: 'Fragment Operations',
    description: 'Re-fetch every loaded fragment series from the server. Use after the user has loaded new fragment data or when the workspace fragment view appears stale.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded for fragments to attach to'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'RefreshAllFragments',
    serverCommand: null,
    jobType: null,
};

export const refreshFragmentInfo = {
    id: 'refresh-fragment-info',
    name: 'Refresh Fragment Info',
    category: 'Fragment Operations',
    description: 'Refresh the metadata for a single fragment (name, energy, hotspot info, clustering state) without re-fetching all fragments. Use when an individual fragment\'s data has been updated server-side.',
    inputs: [
        param('mapCase', 'CaseData', 'The protein case the fragment belongs to', true),
    ],
    outputs: [],
    parameters: [
        param('needToLoadFrags', 'boolean', 'Whether to re-fetch fragment geometry too', true, { defaultValue: false }),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'RefreshFragmentInfo',
    serverCommand: null,
    jobType: null,
};

// ─── Fragment clustering toggle ────────────────────────────────────────────

export const toggleClusteringFragment = {
    id: 'toggle-clustering-fragment',
    name: 'Toggle Fragment Used for Clustering',
    category: 'Fragment Operations',
    description: 'Toggle whether a specific fragment is included in the hotspot-clustering computation. Use when the user wants to add or remove a fragment from the clustering pool that drives hotspot/cluster-map generation.',
    inputs: [
        param('fragInfo', 'object', 'FragInfo for the fragment to toggle', true),
    ],
    outputs: [],
    parameters: [
        param('forceValue', 'boolean', 'Optional explicit value (omit to flip)', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ToggleClusteringFragment',
    serverCommand: null,
    jobType: null,
};

// ─── Default Export ────────────────────────────────────────────────────────

export default [
    loadFragmentSeries,
    refreshAllFragments,
    refreshFragmentInfo,
    toggleClusteringFragment,
];
